import React, { useMemo, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/styles";
import { Container, Typography, Box, Grid, useMediaQuery, Button } from "@mui/material";
import sh from "bundles/common/utils/sh";
import NewJobOfferCard from "bundles/Candidate/components/NewJobOfferCard";
import SquarehubLoader from "./SquarehubLoader";
import {
  ARCHITECTURE_CATEGORIES,
  ENGINEERING_CATEGORIES,
  CONSTRUCTION_CATEGORIES,
  ADMINISTRATION_CATEGORIES,
  INDUSTRY_CATEGORIES,
} from "bundles/common/utils/categoryData.js";
import {
  getEnglishSlug,
} from "bundles/common/utils/urlTranslations";
import BreadcrumbsBar from "./BreadcrumbsBar";
import NoJobsAvailable from "./NoJobsAvailable";
import NotFound from "./NotFound";

const CATEGORIES_BY_INDUSTRY = {
  architecture: ARCHITECTURE_CATEGORIES,
  engineering: ENGINEERING_CATEGORIES,
  construction: CONSTRUCTION_CATEGORIES,
  administration: ADMINISTRATION_CATEGORIES,
  industry: INDUSTRY_CATEGORIES,
};

const CategoryIndex = () => {
  const { industry, category: catString = "" } = useParams();
  const navigate = useNavigate();
  const i18nLocale = useSelector(state => state.i18nLocale);
  const authenticityToken = useSelector(state => state.authenticity_token);
  const intl = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [jobOffers, setJobOffers] = useState([]);

  const catArray = catString.split(",").map(s => s.trim()).filter(Boolean);
  const categoryMap = CATEGORIES_BY_INDUSTRY[industry];
  const categoriesForIndustry = categoryMap?.[i18nLocale] || categoryMap?.en || [];
  const allowedCategorySlugs = new Set(categoriesForIndustry.map(category => category.slug.toLowerCase()));
  const englishCats = catArray.map(slug =>
    getEnglishSlug(industry, slug, i18nLocale)
  );
  const hasInvalidCategory = englishCats.length === 0 ||
    englishCats.some(slug => !allowedCategorySlugs.has(String(slug).toLowerCase()));
  const [categoryLabel, setCategoryLabel] = useState(catString);
  const [loading, setLoading] = useState(true);

  const fetchOffers = () => {
    if (hasInvalidCategory) {
      setLoading(false);
      return;
    }

    setLoading(true);
    sh.get(`/${i18nLocale}/job-offers/fetch`, {
      params: {
        authenticity_token: authenticityToken,
        page,
        industry,
        category: englishCats.join(","),
      },
    })
      .then(res => {
        const offers = res.data?.data || [];
        setJobOffers(prev => page === 1 ? offers : [...prev, ...offers]);
        setPageCount(res.data?.pagy?.pages || 1);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOffers();
  }, [page, industry, catString]);

  useEffect(() => {
    if (hasInvalidCategory) return;

    let arrayForIndustry = [];
    switch (industry) {
    case "architecture":
      arrayForIndustry =
        ARCHITECTURE_CATEGORIES[i18nLocale] || ARCHITECTURE_CATEGORIES["en"];
      break;
    case "engineering":
      arrayForIndustry =
        ENGINEERING_CATEGORIES[i18nLocale] || ENGINEERING_CATEGORIES["en"];
      break;
    case "construction":
      arrayForIndustry =
        CONSTRUCTION_CATEGORIES[i18nLocale] || CONSTRUCTION_CATEGORIES["en"];
      break;
    case "administration":
      arrayForIndustry =
        ADMINISTRATION_CATEGORIES[i18nLocale] ||
        ADMINISTRATION_CATEGORIES["en"];
      break;
    case "industry":
      arrayForIndustry =
        INDUSTRY_CATEGORIES[i18nLocale] ||
        INDUSTRY_CATEGORIES["en"];
      break;
    default:
      arrayForIndustry = [];
      break;
    }

    const found = arrayForIndustry.find(c => c.slug === catString);
    if (found) {
      setCategoryLabel(found.label);
    } else {
      const fallback = catString.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      setCategoryLabel(intl.formatMessage({ id: `categories.${catString}`, defaultMessage: fallback }));
    }
  }, [industry, catString, i18nLocale, hasInvalidCategory]);

  const crumbs = useMemo(() => [
    {
      label: intl.formatMessage({ id: "menu.home" }),
      path: `/${i18nLocale}`,
    },
    {
      label: intl.formatMessage({ id: "nav.job_offers" }),
      path: `/${i18nLocale}/job-offers`,
    },
    {
      label: industry.charAt(0).toUpperCase() + industry.slice(1),
      path: `/${i18nLocale}/job-offers/${industry}`,
    },
    {
      label: categoryLabel,
      path: `/${i18nLocale}/job-offers/${industry}/${catString}`,
      current: true,
    },
  ], [industry, catString, categoryLabel, i18nLocale]);


  const handleOfferClick = offer => {
    if (offer) {
      navigate(`/${i18nLocale}/job-offers/${offer.id}`, { state: { crumbs } });
    }
  };

  if (hasInvalidCategory) {
    return <NotFound />;
  }

  if (!loading && jobOffers.length === 0) {
    return <NoJobsAvailable />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 5, backgroundColor: "#F8F8F8" }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h1">
          {intl.formatMessage(
            { id: "public_job_offer_index.category_index_header" },
            { category: categoryLabel }
          )}
        </Typography>
      </Box>

      <Box>
        <BreadcrumbsBar crumbs={crumbs} />
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          {loading && jobOffers.length === 0 ? (
            <SquarehubLoader size={72} centered />
          ) : (
            <Grid container spacing={2}>
              {jobOffers.map((offer) => (
                <Grid item xs={12} md={isDesktop ? 6 : 12} key={offer.id} sx={{ display: "flex" }}>
                  <NewJobOfferCard offer={offer} onClick={handleOfferClick} />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        {loading && jobOffers.length > 0 ? (
          <SquarehubLoader size={48} />
        ) : !loading && page < pageCount ? (
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#166D4C",
              color: "white",
              borderRadius: "29px",
              px: 23,
              py: 1.5,
              fontSize: "14px",
              fontFamily: "Segoe UI Regular, sans-serif",
              textTransform: "none",
              "&:hover": { backgroundColor: "#135a3d" },
            }}
            onClick={() => setPage(prev => prev + 1)}
          >
            {intl.formatMessage({ id: "see_more" })}
          </Button>
        ) : null}
      </Box>
    </Container>
  );
};

export default CategoryIndex;
